package com.iregained.site.components.sections.resources.sections

import androidx.compose.runtime.Composable
import com.iregained.site.components.sections.resources.styles.*
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Video

// UNIQUE ANIMATION: Elegant Slide Up
val ElegantSlideUpKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateY(40.px) }
    100.percent { Modifier.opacity(1).translateY(0.px) }
}

// Helper modifier to stagger the text entrances
@Composable
private fun Modifier.staggeredGlide(delayMs: Int): Modifier = this.animation(
    ElegantSlideUpKeyframes.toAnimation(
        duration = 1.s,
        timingFunction = AnimationTimingFunction.EaseOut,
        delay = delayMs.ms,
        fillMode = AnimationFillMode.Both // Ensures the element stays perfectly hidden before its delay finishes
    )
)

@Composable
fun ResourcesHeroSection(data: ResourcesHeroSectionData = defaultResourcesHeroData) {

    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "resources-header-section"

    if (data.headline.isNotBlank() || data.videoPath.isNotBlank()) {
        Box(
            modifier = ResourcesHeroContainerStyle.toModifier().id(targetId)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            if (data.videoPath.isNotBlank()) {
                Video(
                    attrs = ResourcesHeroVideoStyle.toModifier().toAttrs {
                        attr("src", data.videoPath)
                        attr("poster", data.backgroundImagePath)
                        attr("playsinline", "")
                        ref { htmlVideoElement ->
                            htmlVideoElement.muted = true
                            htmlVideoElement.loop = true
                            htmlVideoElement.autoplay = true
                            htmlVideoElement.play()
                            onDispose { }
                        }
                    })
            }

            Box(modifier = ResourcesHeroOverlayStyle.toModifier())

            // Removed the animation from the parent Column so we can animate the children individually
            Column(
                modifier = ResourcesHeroContentStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.headline.isNotBlank()) {
                    SpanText(
                        text = data.headline,
                        // ANIMATION: Headline glides in immediately
                        modifier = ResourcesHeroHeadlineStyle.toModifier().staggeredGlide(delayMs = 100)
                    )
                }

                if (data.subHeadline.isNotBlank()) {
                    Div(
                        // ANIMATION: Subheadline waits 400ms, then follows smoothly
                        attrs = ResourcesHeroSubheadlineStyle.toModifier().staggeredGlide(delayMs = 400).toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = data.subHeadline
                                onDispose {}
                            }
                        })
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR RESOURCES HERO:
 * @field backgroundImagePath:
 * - Aspect Ratio: 16:9 or wider.
 * - Recommended Dimensions: 1920x1080px.
 * - Content: Do not use emotional patient photos here. Use a high-fidelity render of the clinical dashboard, abstract data nodes, or a macro shot of the hardware's circuit board/mechatronics. It should feel deeply technical and scientific.
 */
data class ResourcesHeroSectionData(
    val sectionId: String? = null,
    val backgroundImagePath: String = Res.Images.Resources.BANNER,
    val videoPath: String = Res.Videos.ResourcesPage.RESOURCES_VIDEO,
    val headline: String = "Knowledge & Clinical Evidence",
    val subHeadline: String = "Access comprehensive research, including clinical studies, scientific presentations, and documentation supporting the efficacy of the MyHand<sup>®</sup> System."
)

val defaultResourcesHeroData = ResourcesHeroSectionData()